import { Button as MuiButton, ButtonProps as MuiButtonProps, SxProps, Theme } from '@mui/material'
import { useMemo } from 'react'

export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'outline'
}

const BASE_BUTTON_STYLE: SxProps<Theme> = {
  borderRadius: '4px',
  textTransform: 'none',
}

const BUTTON_STYLES: Record<Exclude<ButtonProps['variant'], undefined>, SxProps<Theme>> = {
  // Yellow variant
  primary: {
    ...BASE_BUTTON_STYLE,
    backgroundColor: theme => theme.colors.base[30],
    color: theme => theme.colors.base[80],
    '&:hover': {
      backgroundColor: theme => theme.colors.base[32],
      backgroundImage: `linear-gradient(rgb(0 0 0/10%) 0 0)`,
    },
    '&:disabled': {
      backgroundColor: theme => theme.colors.base[34],
      color: theme => theme.colors.base[80],
    },
  },
  // gray variant
  secondary: {
    ...BASE_BUTTON_STYLE,
    backgroundColor: theme => theme.colors.base[50],
    color: theme => theme.colors.base[10],
    '&:hover': {
      backgroundColor: theme => theme.colors.base[46],
      backgroundImage: `linear-gradient(rgb(0 0 0/10%) 0 0)`,
    },
    '&:disabled': {
      backgroundColor: theme => theme.colors.base[60],
      color: theme => theme.colors.base[45],
    },
  },
  outline: {
    ...BASE_BUTTON_STYLE,
    backgroundColor: 'transparent',
    color: theme => theme.palette.primary.main,
    border: theme => `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: theme => theme.palette.action.hover,
      border: theme => `1px solid ${theme.palette.primary.dark}`,
    },
    '&:disabled': {
      borderColor: theme => theme.colors.base[34],
      color: theme => theme.colors.base[34],
    },
  },
}

export default function Button({ variant = 'primary', sx, ...props }: ButtonProps) {
  const buttonStyles = useMemo(
    () => ({
      ...BUTTON_STYLES[variant],
      ...sx,
    }),
    [variant, sx]
  )

  return <MuiButton variant="text" sx={buttonStyles} {...props} />
}
